/**
 * Reusable filter button component with floating label
 * Used for Account and User filters in Activity and Analytics pages
 */

import React, { ReactElement, MouseEvent, ReactNode, useRef } from "react";
import {
  Button,
  Popover,
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  FormControl,
  InputLabel,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

export interface FilterOption {
  id: string;
  label: string;
  group?: string;
  disabled?: boolean; // Optional: disable this option
}

export interface FloatingLabelFilterButtonProps {
  // Label configuration
  label: string;
  
  // Options and selection
  options: FilterOption[];
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  getDisplayName: (id: string) => string;
  
  // Display text
  allSelectedText: string; // e.g., "All Accounts" or "All Users"
  multipleSelectedText?: (count: number) => string; // e.g., (count) => `${count} Accounts`
  
  // Popover anchor
  anchorEl: HTMLElement | null;
  onOpen: (event: MouseEvent<HTMLElement>) => void;
  onClose: () => void;
  
  // Optional: Group options by a property
  groupOptions?: boolean;
  groupOrder?: string[]; // Order of groups (e.g., ["Cash", "Accounts", "Cards"])
  
  // Optional: Custom popover content
  renderPopoverContent?: (props: {
    options: FilterOption[];
    selectedIds: string[];
    onToggle: (id: string) => void;
    onSelectAll: () => void;
    allSelected: boolean;
    someSelected: boolean;
  }) => ReactNode;
  
  // Optional: Minimum width
  minWidth?: number;
  
  // Optional: Disable entire filter button
  disabled?: boolean;
}

/**
 * Floating label filter button with popover
 */
export default function FloatingLabelFilterButton({
  label,
  options,
  selectedIds,
  onSelectionChange,
  getDisplayName,
  allSelectedText,
  multipleSelectedText,
  anchorEl,
  onOpen,
  onClose,
  groupOptions = false,
  groupOrder = [],
  renderPopoverContent,
  minWidth = 130,
  disabled = false,
}: FloatingLabelFilterButtonProps): ReactElement {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isPopoverOpen = Boolean(anchorEl);

  const handleButtonClick = (event: MouseEvent<HTMLElement>): void => {
    if (disabled) return;
    const targetElement = buttonRef.current || (event.currentTarget as HTMLElement);
    if (!targetElement) {
      console.error("[FloatingLabelFilterButton] No target element available!");
      return;
    }
    event.stopPropagation();
    const syntheticEvent = {
      ...event,
      currentTarget: targetElement,
      target: event.target,
    } as MouseEvent<HTMLElement>;
    try {
      onOpen(syntheticEvent);
    } catch (error) {
      console.error("[FloatingLabelFilterButton] Error calling onOpen:", error);
    }
  };
  
  // Helper to check if user ID is orphaned (starts with "Ao7fhBrt")
  const isOrphanedUserId = (userId: string): boolean => userId.startsWith("Ao7fhBrt");

  // FINAL SAFETY FILTER: Remove orphaned user IDs (starting with "Ao7fhBrt") from options if it's a User filter
  const safeOptions = label === "User" 
    ? options.filter(opt => !isOrphanedUserId(opt.id))
    : options;
  const safeSelectedIds = label === "User"
    ? selectedIds.filter(id => !isOrphanedUserId(id))
    : selectedIds;
  
  // Use safe options and selected IDs (with "Ao7fhBrt" filtered out for User filter)
  const hasValue = safeSelectedIds.length > 0 && safeSelectedIds.length < safeOptions.length;
  const allSelected = safeSelectedIds.length === 0 || safeSelectedIds.length === safeOptions.length;
  const someSelected = safeSelectedIds.length > 0 && safeSelectedIds.length < safeOptions.length;


  const handleToggle = (id: string): void => {
    // Never allow toggling orphaned user IDs
    if (isOrphanedUserId(id)) {
      return;
    }
    // Don't toggle disabled options
    const option = safeOptions.find(opt => opt.id === id);
    if (option?.disabled) {
      return;
    }
    onSelectionChange(
      safeSelectedIds.includes(id)
        ? safeSelectedIds.filter(selectedId => selectedId !== id)
        : [...safeSelectedIds, id]
    );
  };

  const handleSelectAll = (): void => {
    onSelectionChange(
      safeSelectedIds.length === safeOptions.length
        ? []
        : safeOptions.map(opt => opt.id)
    );
  };

  // Get display text for button
  const getButtonText = (): string => {
    // Handle empty options case - always show allSelectedText
    if (safeOptions.length === 0) {
      return allSelectedText;
    }
    if (allSelected) {
      return allSelectedText;
    }
    if (safeSelectedIds.length === 1) {
      return getDisplayName(safeSelectedIds[0]);
    }
    if (multipleSelectedText) {
      return multipleSelectedText(safeSelectedIds.length);
    }
    return `${safeSelectedIds.length} selected`;
  };

  // Render default popover content
  const renderDefaultPopoverContent = (): ReactNode => {
    if (groupOptions && safeOptions.some(opt => opt.group)) {
      // Group options
      const groupedOptions = safeOptions.reduce((acc, option) => {
        const group = option.group || "Other";
        if (!acc[group]) {
          acc[group] = [];
        }
        acc[group].push(option);
        return acc;
      }, {} as Record<string, FilterOption[]>);

      // Sort groups
      const sortedGroups = Object.keys(groupedOptions).sort((a, b) => {
        const indexA = groupOrder.indexOf(a);
        const indexB = groupOrder.indexOf(b);
        if (indexA === -1 && indexB === -1) return a.localeCompare(b);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });

      // If only one option, disable unchecking
      const hasOnlyOneOption = safeOptions.length === 1;
      
      return (
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleSelectAll}
                disabled={hasOnlyOneOption}
              />
            }
            label={<Typography variant="body2">{allSelectedText}</Typography>}
            sx={{ mb: 0.5 }}
          />
          <Divider sx={{ my: 0.5 }} />
          {sortedGroups.map((group) => (
            <Box key={group} sx={{ mb: 0.5 }}>
              <Typography
                variant="caption"
                fontWeight="medium"
                color="text.secondary"
                sx={{ px: 1, py: 0.25, display: "block" }}
              >
                {group}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", width: "100%", minWidth: 0 }}>
                {groupedOptions[group].map((option) => (
                  <FormControlLabel
                    key={option.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={safeSelectedIds.includes(option.id)}
                        onChange={() => handleToggle(option.id)}
                        disabled={(hasOnlyOneOption && safeSelectedIds.includes(option.id)) || option.disabled}
                      />
                    }
                    label={
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: "0.875rem", 
                          overflow: "hidden", 
                          textOverflow: "ellipsis", 
                          whiteSpace: "nowrap",
                          opacity: option.disabled ? 0.5 : 1,
                          fontStyle: option.disabled ? "italic" : "normal"
                        }}
                      >
                        {option.label}
                      </Typography>
                    }
                    sx={{ ml: 1.5, py: 0.25, width: "100%", display: "flex", minWidth: 0 }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </FormGroup>
      );
    }

    // Simple list (no grouping)
    // If only one option, disable unchecking
    const hasOnlyOneOption = safeOptions.length === 1;
    
    return (
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleSelectAll}
              disabled={hasOnlyOneOption}
            />
          }
          label={<Typography variant="body2">{allSelectedText}</Typography>}
          sx={{ mb: 0.5 }}
        />
        <Divider sx={{ my: 0.5 }} />
        {safeOptions.map((option) => {
          // Double-check: never render orphaned user IDs
          if (isOrphanedUserId(option.id)) {
            return null;
          }
          return (
            <FormControlLabel
              key={option.id}
              control={
                <Checkbox
                  size="small"
                  checked={safeSelectedIds.includes(option.id)}
                  onChange={() => handleToggle(option.id)}
                  disabled={(hasOnlyOneOption && safeSelectedIds.includes(option.id)) || option.disabled}
                />
              }
              label={
                <Typography 
                  variant="body2"
                  sx={{
                    opacity: option.disabled ? 0.5 : 1,
                    fontStyle: option.disabled ? "italic" : "normal"
                  }}
                >
                  {option.label}
                </Typography>
              }
            />
          );
        })}
      </FormGroup>
    );
  };

  return (
    <>
      <FormControl 
        size="small" 
        fullWidth
        sx={{ 
          position: "relative", 
          minWidth,
        }}
      >
        <InputLabel 
          shrink={true}
          sx={{
            transform: "translate(14px, -7px) scale(0.75)",
            transformOrigin: "top left",
            transition: "transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms",
            pointerEvents: "none",
            backgroundColor: "background.paper",
            px: 0.5,
            zIndex: 3, // Ensure label is above border cutout
          }}
        >
          {label}
        </InputLabel>
        <Box
          sx={{
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: "-1px", // Position at top border
              left: "10px", // Start where label begins (14px translate - 4px padding)
              width: `calc(${label.length * 0.5}em + 10px)`, // Approximate label width: ~0.5em per char + padding
              height: "2px", // Cover the border width (1px border + 1px overlap)
              backgroundColor: "background.paper", // Match background to create cutout
              zIndex: 2,
              pointerEvents: "none",
              transition: "background-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
            },
            // Update cutout background when hovering over the box (which contains the button)
            "&:hover::before": {
              backgroundColor: (theme) => 
                disabled 
                  ? theme.palette.background.paper 
                  : theme.palette.action.hover, // Match hover background
            },
          }}
        >
          <Button
            ref={buttonRef}
            fullWidth
            variant="outlined"
            data-testid={`filter-button-${label.toLowerCase()}`}
            data-disabled={disabled}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!disabled) handleButtonClick(e);
            }}
            size="small"
            disableRipple={false}
            disabled={disabled}
            sx={{ 
              height: "40px", 
              justifyContent: "space-between",
              alignItems: "center",
              outline: "none !important",
              borderColor: disabled ? "rgba(0, 0, 0, 0.12)" : "rgba(0, 0, 0, 0.23)",
              pointerEvents: disabled ? "none" : "auto",
              cursor: disabled ? "default" : "pointer",
              touchAction: "manipulation", // Ensure touch events work on mobile
              WebkitTapHighlightColor: "transparent",
              position: "relative",
              zIndex: 1, // Button is below cutout overlay
              opacity: disabled ? 0.5 : 1,
            "&:focus": {
              outline: "none !important",
              boxShadow: "none",
              borderColor: "rgba(0, 0, 0, 0.23) !important",
            },
            "&:focus-visible": {
              outline: "none !important",
              boxShadow: "none",
              borderColor: "rgba(0, 0, 0, 0.23) !important",
            },
            "&:hover": {
              boxShadow: "none",
              borderColor: disabled ? "rgba(0, 0, 0, 0.23)" : "rgba(0, 0, 0, 0.87)",
              backgroundColor: disabled ? "transparent" : "action.hover",
            },
            "&.Mui-focusVisible": {
              outline: "none !important",
              boxShadow: "none",
              borderColor: "rgba(0, 0, 0, 0.23) !important",
            },
            "&.Mui-focused": {
              outline: "none !important",
              boxShadow: "none",
              borderColor: "rgba(0, 0, 0, 0.23) !important",
            },
            "&.Mui-disabled": {
              cursor: "default",
              pointerEvents: "none",
            },
          }}
        >
          <Typography 
            variant="body2" 
            noWrap
            sx={{
              color: "text.primary",
              fontSize: "0.875rem",
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            {getButtonText()}
          </Typography>
          <ArrowDropDownIcon 
            sx={{ 
              ml: 1,
              color: "text.secondary",
              fontSize: "1.6rem",
            }}
          />
        </Button>
        </Box>
      </FormControl>
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={() => onClose()}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          zIndex: 1300, // Ensure popover is above other elements
        }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
        <Box sx={{ p: 1, minWidth: 200, maxWidth: 280, maxHeight: 350, overflowY: "auto", overflowX: "hidden" }}>
          {renderPopoverContent 
            ? renderPopoverContent({
                options,
                selectedIds,
                onToggle: handleToggle,
                onSelectAll: handleSelectAll,
                allSelected,
                someSelected,
              })
            : renderDefaultPopoverContent()}
        </Box>
      </Popover>
    </>
  );
}
